import { useEffect, useMemo, useState } from "react";
import { HttpMethod, callApi } from "src/services/apiService";
import { useAppSelector } from "src/app/hooks";
import { selectAuth } from "src/features/authSlice";
import AccountUsagePanel from "src/components/AccountUsagePanel";

/**
 * Account Usage report — three "live counts this billing period" cards
 * (DNC / Reassigned / TrustCall) rendered by the shared AccountUsagePanel,
 * plus two self-service IP-address-report cards (DNC + Reassigned) that
 * email the requester a list of IPs that scrubbed in a date range.
 * Replaces manual support queries.
 */
const AccountUsage = () => {
  const { userEmail } = useAppSelector(selectAuth);

  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const thirtyDaysAgo = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
  }, []);

  // Gate the Reassigned IP card to accounts that actually have at least one
  // reassigned product enabled. Reuses the existing /ReassignedUsage endpoint
  // (the same one AccountUsagePanel already calls) — a separate fetch here
  // is cheap and keeps the page composition simple.
  const [showReassignedIpCard, setShowReassignedIpCard] = useState(false);
  useEffect(() => {
    (async () => {
      try {
        const r = (await callApi(
          "admin/Account/ReassignedUsage",
          HttpMethod.GET,
        )) as { anyFeatureEnabled?: boolean };
        setShowReassignedIpCard(!!r?.anyFeatureEnabled);
      } catch {
        setShowReassignedIpCard(false);
      }
    })();
  }, []);

  return (
    <div className="container-fluid p-3">
      <h1>Account Usage</h1>
      <p className="text-muted">Live counts for the current billing period.</p>
      <AccountUsagePanel endpointBase="admin/Account" showDncEmailForm />

      <IpAddressCard
        title="DNC Scrub IP Addresses"
        description={
          <>
            Email yourself a list of IP addresses that scrubbed Do Not Call
            records on this account in the selected date range.{" "}
            <strong>Reassigned Number scrubs are not included.</strong>
          </>
        }
        endpoint="admin/Account/IPAddressesEmail"
        defaultEmail={userEmail ?? ""}
        defaultStart={thirtyDaysAgo}
        defaultEnd={today}
        today={today}
      />

      {showReassignedIpCard && (
        <IpAddressCard
          title="Reassigned Scrub IP Addresses"
          description={
            <>
              Email yourself a list of IP addresses used for Reassigned Number
              scrubs (TCPA Authority / AuthorityPlus / Basic RND) in the
              selected date range. <strong>Do Not Call scrubs are not included.</strong>
            </>
          }
          endpoint="admin/Account/ReassignedIPAddressesEmail"
          defaultEmail={userEmail ?? ""}
          defaultStart={thirtyDaysAgo}
          defaultEnd={today}
          today={today}
        />
      )}
    </div>
  );
};

interface IpAddressCardProps {
  title: string;
  description: React.ReactNode;
  endpoint: string;
  defaultEmail: string;
  defaultStart: string;
  defaultEnd: string;
  today: string;
}

/**
 * Date-range + email form that POSTs to a "background-job" endpoint and
 * swaps to a confirmation message on 202. Used twice on this page — once
 * for DNC scrub IPs (Sessions table) and once for Reassigned scrub IPs
 * (TCPAAuthoritySessions). Same UX, different endpoint + scope copy.
 */
const IpAddressCard: React.FC<IpAddressCardProps> = ({
  title,
  description,
  endpoint,
  defaultEmail,
  defaultStart,
  defaultEnd,
  today,
}) => {
  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(defaultEnd);
  const [email, setEmail] = useState(defaultEmail);
  const [status, setStatus] = useState<"idle" | "sending" | "queued" | "error">("idle");
  const [message, setMessage] = useState("");

  // Range is capped at 60 days client-side (backend mirrors the check).
  // Whole-day diff including both endpoints — picking same start+end = 1 day.
  const dayDiffInclusive = (() => {
    if (!startDate || !endDate) return 0;
    const ms = new Date(endDate).getTime() - new Date(startDate).getTime();
    return Math.floor(ms / (1000 * 60 * 60 * 24)) + 1;
  })();
  const rangeTooLong = dayDiffInclusive > 60;

  const send = async () => {
    setStatus("sending");
    setMessage("");
    try {
      // Bump end date by one day so SQL's half-open `< @end` includes the
      // user's picked end day.
      const endPlusOne = new Date(endDate);
      endPlusOne.setDate(endPlusOne.getDate() + 1);
      const endExclusive = endPlusOne.toISOString().slice(0, 10);

      await callApi(endpoint, HttpMethod.POST, {
        email: email.trim(),
        startDate,
        endDate: endExclusive,
      });
      setStatus("queued");
    } catch (err: any) {
      setStatus("error");
      setMessage(err?.message ?? "Failed to queue report.");
    }
  };

  return (
    <div className="card mb-3">
      <div className="card-header">{title}</div>
      <div className="card-body">
        <p className="text-muted mb-3" style={{ fontSize: "0.85rem" }}>
          {description}
        </p>
        {status === "queued" ? (
          <div className="text-muted" style={{ fontSize: "0.9rem" }}>
            Report queued for <code>{email}</code>. Check your inbox in a
            minute.
          </div>
        ) : (
          <>
            <div className="d-flex flex-wrap align-items-end gap-3">
              <div>
                <label className="form-label mb-1" style={{ fontSize: "0.85rem" }}>
                  Start date
                </label>
                <input
                  type="date"
                  className="form-control form-control-sm"
                  style={{ maxWidth: 180 }}
                  value={startDate}
                  max={endDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  disabled={status === "sending"}
                />
              </div>
              <div>
                <label className="form-label mb-1" style={{ fontSize: "0.85rem" }}>
                  End date
                </label>
                <input
                  type="date"
                  className="form-control form-control-sm"
                  style={{ maxWidth: 180 }}
                  value={endDate}
                  min={startDate}
                  max={today}
                  onChange={(e) => setEndDate(e.target.value)}
                  disabled={status === "sending"}
                />
              </div>
              <div className="flex-grow-1" style={{ maxWidth: 320 }}>
                <label className="form-label mb-1" style={{ fontSize: "0.85rem" }}>
                  Email to
                </label>
                <input
                  type="email"
                  className="form-control form-control-sm"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={status === "sending"}
                />
              </div>
              <button
                type="button"
                className="btn btn-sm btn-outline-primary"
                disabled={
                  status === "sending" ||
                  !email.trim() ||
                  !startDate ||
                  !endDate ||
                  endDate < startDate ||
                  rangeTooLong
                }
                onClick={send}
              >
                Send report
              </button>
            </div>
            {rangeTooLong && (
              <div className="text-danger mt-2" style={{ fontSize: "0.85rem" }}>
                Date range cannot exceed 60 days (selected:{" "}
                {dayDiffInclusive.toLocaleString()} days).
              </div>
            )}
            {status === "error" && (
              <div className="text-danger mt-2" style={{ fontSize: "0.85rem" }}>
                {message}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AccountUsage;
